import { Link, useLocation } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useLogout } from '@/features/logout';
import { useAuthStore } from '@/shared/store/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';

/** 도움말 아이콘 */
function HelpIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 20.5C9.74566 20.5 7.58365 19.6045 5.98959 18.0104C4.39553 16.4163 3.5 14.2543 3.5 12C3.5 9.74566 4.39553 7.58365 5.98959 5.98959C7.58365 4.39553 9.74566 3.5 12 3.5C14.2543 3.5 16.4163 4.39553 18.0104 5.98959C19.6045 7.58365 20.5 9.74566 20.5 12C20.5 14.2543 19.6045 16.4163 18.0104 18.0104C16.4163 19.6045 14.2543 20.5 12 20.5ZM12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22ZM12 16C11.7348 16 11.4804 16.1054 11.2929 16.2929C11.1054 16.4804 11 16.7348 11 17C11 17.2652 11.1054 17.5196 11.2929 17.7071C11.4804 17.8946 11.7348 18 12 18C12.2652 18 12.5196 17.8946 12.7071 17.7071C12.8946 17.5196 13 17.2652 13 17C13 16.7348 12.8946 16.4804 12.7071 16.2929C12.5196 16.1054 12.2652 16 12 16ZM12.053 12.467C12.3401 12.1887 12.6476 11.9323 12.973 11.7C13.2354 11.514 13.4846 11.3102 13.719 11.09C13.9104 10.9069 14.0687 10.692 14.187 10.455C14.313 10.2017 14.3757 9.90733 14.375 9.572C14.375 9.19867 14.293 8.84933 14.129 8.524C13.971 8.2 13.714 7.936 13.358 7.732C13.002 7.52867 12.548 7.427 11.996 7.427C11.4213 7.427 10.951 7.54267 10.585 7.774C10.2382 7.98395 9.9634 8.29433 9.797 8.664C9.737 8.798 9.68767 8.93567 9.649 9.077C9.538 9.491 9.204 9.844 8.775 9.844C8.347 9.844 7.991 9.494 8.065 9.072C8.13567 8.66533 8.26433 8.282 8.451 7.922C8.75233 7.33867 9.209 6.874 9.821 6.528C10.435 6.176 11.1847 6 12.07 6C13 6 13.7577 6.18133 14.343 6.544C14.933 6.90133 15.3567 7.347 15.614 7.881C15.8713 8.415 16 8.951 16 9.489C16 10.0337 15.9153 10.5013 15.746 10.892C15.576 11.2767 15.3707 11.59 15.13 11.832C14.8947 12.074 14.5803 12.3463 14.187 12.649C13.875 12.897 13.626 13.1087 13.44 13.284C13.26 13.46 13.1097 13.669 12.989 13.911C12.939 14.0103 12.9013 14.111 12.876 14.213C12.768 14.637 12.454 15 12.016 15C11.579 15 11.21 14.644 11.256 14.21C11.3203 13.5565 11.6007 12.9431 12.053 12.467Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** 사용자 프로필 아이콘 */
function ProfileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 0C4.477 0 0 4.477 0 10C0 15.523 4.477 20 10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0ZM4.858 16.752C5.463 14.884 7.58 13.512 10 13.512C12.42 13.512 14.537 14.884 15.142 16.752C13.712 17.844 11.933 18.5 10 18.5C8.067 18.5 6.288 17.844 4.858 16.752ZM16.327 15.657C15.307 13.492 12.844 12.012 10 12.012C7.156 12.012 4.693 13.492 3.673 15.657C2.27498 14.1043 1.50095 12.0893 1.5 10C1.5 5.313 5.313 1.5 10 1.5C14.687 1.5 18.5 5.313 18.5 10C18.499 12.0893 17.725 14.1043 16.327 15.657ZM10 4C9.54037 4 9.08525 4.09053 8.66061 4.26642C8.23597 4.44231 7.85013 4.70012 7.52513 5.02513C7.20012 5.35013 6.94231 5.73597 6.76642 6.16061C6.59053 6.58525 6.5 7.04037 6.5 7.5C6.5 7.95963 6.59053 8.41475 6.76642 8.83939C6.94231 9.26403 7.20012 9.64987 7.52513 9.97487C7.85013 10.2999 8.23597 10.5577 8.66061 10.7336C9.08525 10.9095 9.54037 11 10 11C10.9283 11 11.8185 10.6313 12.4749 9.97487C13.1313 9.3185 13.5 8.42826 13.5 7.5C13.5 6.57174 13.1313 5.6815 12.4749 5.02513C11.8185 4.36875 10.9283 4 10 4ZM10 9.5C8.897 9.5 8 8.603 8 7.5C8 6.397 8.897 5.5 10 5.5C11.103 5.5 12 6.397 12 7.5C12 8.603 11.103 9.5 10 9.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** 인증이 필요한 라우트인지 확인 */
function isProtectedRoute(pathname: string): boolean {
  // /account만 보호됨 (에이전트 목록/상세는 공개)
  return pathname.startsWith('/account');
}

export function ActionButtons() {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const isProtected = isProtectedRoute(location.pathname);

  // 보호된 페이지(account)에서 로그아웃 → /agents로 이동
  // 공개 페이지에서 로그아웃 → 현재 페이지 유지
  const { handleLogout, isPending: isLoggingOut } = useLogout({
    redirectTo: isProtected ? '/agents' : false,
    onSuccess: () => {
      toast.success('로그아웃 되었습니다');
    },
    onError: () => {
      toast.error('로그아웃 중 오류가 발생했습니다');
    },
  });

  return (
    <div className="inline-flex items-center justify-end gap-4">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="relative text-black" aria-label="도움말">
              <HelpIcon />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>준비중입니다</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {isAuthenticated ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="relative text-black" aria-label="프로필">
              <ProfileIcon />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>메뉴</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/account" className="cursor-pointer">
                계정 관리
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              {isLoggingOut ? '로그아웃 중...' : '로그아웃'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Link
          to="/login"
          search={{ redirect: location.pathname }}
          className="relative text-black hover:opacity-70 transition-opacity"
          aria-label="로그인"
        >
          <ProfileIcon />
        </Link>
      )}
    </div>
  );
}
